import { useEffect, useCallback } from 'react'
import {
  usePostEnginePlayNew,
  usePostEnginePlayDelete,
  usePostEnginePlaySessionsSessionIdRestart,
  useGetMemorySessionsSessionId,
  usePostChatSessionsSessionIdChoice,
  useGetChatSessionsSessionIdChoiceTaskTaskId,
  usePostEnginePlayPreview,
  usePostEnginePlaySessionsSessionIdRegenerate,
  usePostEnginePlaySessionsSessionIdMessagesMsgIdDelete,
} from '@/types/origin-point-engine'
import { logger } from '@/lib/logger'
import { showErrorData, showSuccessData } from '@/lib/toast'
import { useEngineStore } from '../stores/use-engine-store'
import type { SessionMemoryResponse } from '../types/memory'
import type {
  EnginePlaySnapshotResponse,
  CurrentScene,
  ChoiceTaskStatus,
} from '../types/type'
import { useInfiniteMessages } from './use-infinite-messages'

interface UseEnginePlayOptions {
  /**
   * 是否跳过初始的场景/记忆数据获取
   * - true: 从 startSession 新建会话进入时，数据已在 mutation 响应中获取，无需再次请求
   * - false: 从会话列表恢复时，需要通过 Query 获取缓存/最新数据
   */
  skipInitialFetch?: boolean
}

// 轮询间隔 (毫秒)
const POLLING_INTERVAL = 10000

export const useEnginePlay = (
  sessionId?: string,
  options: UseEnginePlayOptions = {}
) => {
  const { skipInitialFetch = false } = options

  // 只有在 sessionId 存在且不跳过初始获取时，才启用 Query
  const queryEnabled = !!sessionId && !skipInitialFetch

  // ========== 从 Store 获取轮询状态 ==========
  const {
    taskId,
    expectedTaskId,
    isPolling,
    startPolling,
    stopPolling,
    setPendingResult,
  } = useEngineStore((s) => s.choice)

  // 1. 使用 InfiniteQuery 获取分页消息列表 (GET /engine-play/sessions/{sessionId}/messages)
  const {
    flatMessages: messages,
    sessionInfo,
    hasMore,
    isPending: isMessagesLoading,
    isFetchingNextPage: isLoadingMore,
    fetchNextPage,
    refetch: refetchMessages,
  } = useInfiniteMessages(sessionId!, { enabled: queryEnabled })

  // 2. 获取会话记忆数据 (GET /memory/sessions/{sessionId})
  const {
    data: sessionMemory,
    isLoading: isMemoryLoading,
    refetch: refetchMemory,
  } = useGetMemorySessionsSessionId(
    sessionId!,
    {},
    {
      query: {
        enabled: queryEnabled,
        select: (data) => {
          const response = data as unknown as SessionMemoryResponse
          return response.data
        },
      },
    }
  )

  // 3. 轮询任务状态 (GET /chat/sessions/{sessionId}/choice/task/{taskId})
  const { data: taskStatusData, dataUpdatedAt } =
    useGetChatSessionsSessionIdChoiceTaskTaskId(sessionId!, taskId!, {
      query: {
        enabled: !!sessionId && !!taskId && isPolling,
        refetchInterval: POLLING_INTERVAL,
        select: (data) => {
          // 根据实际 API 响应结构：taskId, status 在顶层，currentScene 在 data.data 中
          const result = {
            // @ts-expect-error 实际响应中 taskId 在顶层，与生成的类型定义不符
            taskId: data.taskId as string,
            // @ts-expect-error 实际响应中 currentScene 位于 data.data，与生成的类型定义不符
            currentScene: data.data?.currentScene as CurrentScene,
            // @ts-expect-error 实际响应中 status 在顶层，与生成的类型定义不符
            status: data.status as ChoiceTaskStatus,
          }
          if (result.status === 'failed') {
            stopPolling()
            logger.error('[轮询] 选择任务失败')
            // @ts-expect-error 实际响应包含 error 字段，与生成的类型定义不符
            showErrorData('消息接收失败：', data.error)
          }
          logger.debug('[轮询] select 解析结果:', result)
          return result
        },
      },
    })

  // 监听任务状态变化，处理轮询结果
  // 当轮询完成时，将结果存储到 store 的 pendingResult 中
  useEffect(() => {
    if (!taskStatusData || !isPolling) return

    // 验证返回的数据是否属于当前期望的 taskId
    if (taskStatusData.taskId !== expectedTaskId) {
      logger.debug(
        '[轮询] 忽略旧的 taskStatusData，期望:',
        expectedTaskId,
        '实际:',
        taskStatusData.taskId
      )
      return
    }

    logger.debug('[轮询] 处理任务状态', {
      status: taskStatusData.status,
      taskId: taskStatusData.taskId,
      isPolling,
    })

    const status = taskStatusData.status as ChoiceTaskStatus

    if (status === 'completed') {
      const result = taskStatusData.currentScene as CurrentScene | undefined
      if (result) {
        logger.info('[轮询] 任务完成，将结果存入 store', result)
        // 先存储结果，再停止轮询
        // 这样 usePollingHandler 可以在 isPolling 变 false 之前处理结果
        // 避免其他依赖 !isPolling 的 effect（如 initializeFromCache）抢先执行
        setPendingResult(result)
      }
      stopPolling()
    } else if (status === 'failed') {
      stopPolling()
      logger.error('[轮询] 选择任务失败')
    }
  }, [
    taskStatusData,
    dataUpdatedAt,
    isPolling,
    expectedTaskId,
    stopPolling,
    setPendingResult,
  ])

  // 4. 开始新游玩 (POST /engine-play/new)
  const { mutateAsync: startPlayAsync, isPending: isStarting } =
    usePostEnginePlayNew({
      mutation: {
        onSuccess: (data) => {
          const response = data
          logger.debug('开始游玩成功', response.msg)
          return response.data
        },
        onError: (error) => {
          logger.error('开始游玩失败', error)
        },
      },
    })

  // 4. 从编辑器中进入会话（预览）：如果是新开的引擎，无之前的会话，则调用这个接口，否则调用/engine-play/sessions/{sessionId}/message
  const { mutateAsync: startPreviewAsync, isPending: isPreviewing } =
    usePostEnginePlayPreview({
      mutation: {
        onSuccess: (data) => {
          const response = data
          logger.debug('开始预览成功', response.msg)
          return response.data
        },
        onError: (error) => {
          logger.error('开始预览失败', error)
        },
      },
    })

  // 5. 做出选择 (POST /chat/sessions/{sessionId}/choice)
  // 注意：onSuccess 需要从 variables 获取 sessionId，避免闭包捕获旧值
  const { mutateAsync: makeChoiceAsyncRaw, isPending: isChoosing } =
    usePostChatSessionsSessionIdChoice({
      mutation: {
        onSuccess: (data, variables) => {
          const response = data
          // 从 variables 中获取 sessionId，避免闭包陷阱
          const currentSessionId = variables.sessionId
          logger.debug('[useEnginePlay] 选择提交成功，响应:', response)
          // @ts-expect-error 实际响应在顶层返回 taskId，与生成的类型定义不符
          const newTaskId = response?.taskId
          logger.debug('[useEnginePlay] 解析到的 taskId:', newTaskId)

          if (newTaskId && currentSessionId) {
            logger.debug(
              '[useEnginePlay] 开始轮询，taskId:',
              newTaskId,
              'sessionId:',
              currentSessionId
            )
            startPolling(newTaskId, currentSessionId)
          } else {
            logger.warn(
              '[useEnginePlay] 未获取到 taskId 或 sessionId，无法开始轮询'
            )
          }
          return response
        },
        onError: (error) => {
          logger.error('选择提交失败', error)
        },
      },
    })

  //  删除最后一条AI回复并基于上一条用户输入重新生成（含自动扣费）
  // /play/sessions/{id}/regenerate [post]
  // 注意：onSuccess 需要从 variables 获取 sessionId，避免闭包捕获旧值
  const { mutateAsync: regenerateAsync, isPending: isRegenerating } =
    usePostEnginePlaySessionsSessionIdRegenerate({
      mutation: {
        onSuccess: (data, variables) => {
          const response = data
          // 从 variables 中获取 sessionId，避免闭包陷阱
          const currentSessionId = variables.sessionId
          logger.debug(
            '删除最后一条AI回复并基于上一条用户输入重新生成成功',
            response.msg
          )
          // @ts-expect-error 实际响应可能在顶层或 data 中返回 taskId，与生成的类型定义不符
          const newTaskId = response?.taskId ?? response?.data?.taskId
          logger.debug('[useEnginePlay] regenerate 解析到的 taskId:', newTaskId)

          if (newTaskId && currentSessionId) {
            logger.debug(
              '[useEnginePlay] 开始轮询（regenerate），taskId:',
              newTaskId,
              'sessionId:',
              currentSessionId
            )
            startPolling(newTaskId, currentSessionId)
          } else {
            logger.warn(
              '[useEnginePlay] regenerate 未获取到 taskId 或 sessionId，无法开始轮询'
            )
          }
          return response.data
        },
        onError: (error) => {
          logger.error(
            '删除最后一条AI回复并基于上一条用户输入重新生成失败',
            error
          )
          showErrorData('重新生成失败', error)
        },
      },
    })

  // //删除会话中的指定消息
  // // /play/sessions/{id}/messages/{msgId} [delete]
  const { mutateAsync: deleteMessageAsync, isPending: isDeletingMessage } =
    usePostEnginePlaySessionsSessionIdMessagesMsgIdDelete({
      mutation: {
        onSuccess: (data) => {
          const response = data
          logger.debug('删除会话中的指定消息成功', response.msg)
          showSuccessData('删除消息成功')
          return response.data
        },
        onError: (error) => {
          logger.error('删除会话中的指定消息失败', error)
          showErrorData('删除消息失败', error)
        },
      },
    })

  /**
   * 封装后的 makeChoiceAsync
   * 不再等待轮询完成，立即返回。结果通过 store.choice.pendingResult 传递
   */
  const makeChoiceAsync = useCallback(
    async (variables: {
      sessionId: string
      data: { choiceId: string; customText?: string }
    }) => {
      try {
        await makeChoiceAsyncRaw(variables)
        return null
      } catch (error) {
        logger.error('makeChoiceAsync 失败', error)
        return null
      }
    },
    [makeChoiceAsyncRaw]
  )

  // 6. 重新开始会话
  const { mutateAsync: restartSessionAsync } =
    usePostEnginePlaySessionsSessionIdRestart({
      mutation: {
        onSuccess: (data) => {
          const response = data as unknown as EnginePlaySnapshotResponse
          logger.debug('会话已重置', response.data)
          return response.data
        },
      },
    })

  // 7. 结束会话
  const { mutateAsync: endSessionAsync } = usePostEnginePlayDelete({
    mutation: {
      onSuccess: (data) => {
        const response = data
        logger.debug('会话已结束')
        return response.data
      },
    },
  })

  return {
    // 分页消息数据

    messages,
    sessionInfo,
    hasMore,
    isMessagesLoading,
    isLoadingMore,
    fetchNextPage,

    // 其他数据
    memory: sessionMemory,
    isMemoryLoading,
    isStarting,
    isPreviewing,
    isChoosing,
    isPolling,
    isRegenerating,
    isDeletingMessage,

    // Mutations
    startPlayAsync,
    startPreviewAsync,
    makeChoiceAsync,
    restartSessionAsync,
    endSessionAsync,
    regenerateAsync,
    deleteMessageAsync,

    // 刷新方法
    refreshAllData: () => {
      refetchMessages()
      refetchMemory()
    },
    refetchMemory,
    refetchMessages,
  }
}
